import { Component, Element, EventEmitter, Event, h, Host, Method, Prop, State, Listen } from '@stencil/core';
import { TiMetricsActionEventDetail } from '@ticom/web-component-utils';
import { MediaGalleryItemChangeEventDetail, MediaGalleryItemControlEventDetail } from '../media-gallery/media-gallery.interface';
import { Video, VideoPlayerStatus } from './video-player-interface';

/**
 * @slot - The default slot is for the Brightcove `video-js` element.
 * @slot title - Title shown above the video player.
 */
@Component({
    tag: 'ti-video-player',
    styleUrl: 'video-player.scss',
    shadow: true
})
export class VideoPlayer {

    /**
     * TI Brightcove account ID - used to pull the brightcove scripts.
     * Account ID is same for all the player experiences on TI.com
     *
     * @type {string}
     * @default '3816841626001'
     */
    private _accountId: string = '3816841626001';

    /**
     * intersection observer
     */
    private _intersectionObserver: IntersectionObserver;

    /**
     * State of video fullscreen mode.
     */
    private _isFullscreen: boolean = false;

    /**
     * ti-progress container
     */
    private _loadingContainer: HTMLDivElement;

    /**
     * video-js container
     */
    private _playerContainer: HTMLDivElement;

    /**
     * List of videos from video service
     *
     * @type {Array}
     * @default null
     **/
    private _videoList: Array<Video>;

    /**
     * Reference to host HTML element.
     */
    @Element() hostElement: HTMLElement;

    /**
     * Locale used in the link to the video transcript page.
     */
    @Prop() locale: string = 'en-US';

    /**
     * Event emitted for metrics
    */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    /**
     * Event emitted after video has finished initializing to indicate
     * whether or not the video should be disabled because it has no data.
    */
    @Event() tiMediaGalleryItemChange: EventEmitter<MediaGalleryItemChangeEventDetail>;

    /**
     * Use brightcove API to detect when video is done loading
     * to hide progress wheel and show video
     */
    @Method()
    async initializeVideo() {
        const videoEl = this.hostElement.querySelector('video-js');
        if (!videoEl) {
            return false;
        }
        const vpId = videoEl.getAttribute('id');

        // Brightcove player with Playlist
        // window['bcov'] is the script loaded for Brightcove with playlist experience
        // players.brightcove.net/3816841626001/experience_5ec5ac67a47fd100255e9b68/live.js
        if (!this.singleVideoPlayer) {
            const bcovClientApi = window['bcov']?.gal.getEmbeds()[vpId].clientApi;
            // Verify if the brightcove player is loaded
            bcovClientApi?.once('playerLoaded', () => {
                // IE11/Edge Fix: hide progress spinner on READY state
                // and do not modify the 'status' state variable as it removes the brightcove iframe on re-render
                this._loadingContainer.style.display = 'none';
                this._playerContainer.setAttribute('class', 'ti-video-player-show-player');
            });
        }
        // Brightcove player single video
        // window['videojs'] is the module bundled with Brightcove for single player experience and includes the api used
        // to indicate when the video is loaded and ready
        // players.brightcove.net/3816841626001//sZbarZzBuz_default/index.min.js
        else {
            // Verify brightcove player is loaded using api
            window['videojs']?.getPlayer(vpId).ready(() => {
                this._trackVideoLoadedMetrics();
                this._loadingContainer.style.display = 'none';
                this._playerContainer.setAttribute('class', 'ti-video-player-show-player');
            });
        }
    }

    /**
     * Keep track of component status
     *
     * @type {string}
     * @default 'LOADING'
     */
    @State() status: string = VideoPlayerStatus.LOADING;

    /**
     * Video Service URL
     *
     * @type {string}
     * @default ''
     */
    @Prop() apiUrl: string = '';

    /**
     * TI Brightcove experience ID - used to pull the right brightcove player experience.
     * New "in-page responsive gallery" experience that includes the playlist on the right side uses below id.
     *
     * @type {string}
     * @default '5ec5ac67a47fd100255e9b68'
     */
    @Prop() experienceId: string = '5ec5ac67a47fd100255e9b68';

    /**
     * Flag to check lazy load
     */
    @Prop() lazy: boolean = true;

    /**
     * TI Brightcove Single Video player - bright cove default player only
     *
     * @type {boolean}
     * @default false
     */
    @Prop() singleVideoPlayer: boolean = false;

    /**
     * Components lifecycle hooks
     */
    componentWillLoad() {
        if ((!this.apiUrl || this.apiUrl === '') && !(this.hostElement.querySelector<HTMLScriptElement>('script[type=\'application/json\']'))) {
            // no api-url - set status to error;
            this.status = VideoPlayerStatus.ERROR;
        }
    }

    componentDidLoad() {
        // add io if status is not error (i.e., api-url is valid)
        if(this.status !== VideoPlayerStatus.ERROR) {
            this._addIntersectionObserver();
        }
        if (!this.lazy) {
            this._getVideos();
        }
    }

    disconnectedCallback() {
        // remove io
        this._removeIntersectionObserver();
    }

    /**
     * Listen for fullscreenchange event from Brightcove video player and emit media change event.
     */
    @Listen('fullscreenchange')
    onFullscreenchange() {
        // toggle state
        this._isFullscreen = !this._isFullscreen;
        this.tiMediaGalleryItemChange.emit({ fullscreen: this._isFullscreen });
    }

    /**
     * Listen for control events from ti-media-gallery to play/pause the video and optionally go fullscreen.
     */
    @Listen('tiMediaGalleryItemControl')
    onMediaGalleryItemControl(ev: CustomEvent<MediaGalleryItemControlEventDetail>) {
        if (ev.detail.action === 'pause') {
            // pause all video
            window['videojs']?.getAllPlayers().forEach((el) => {
                el.pause();
            });
        } else if (ev.detail.action === 'play') {
            // Using video-js api instead of HTML5 api because iOS doesn't support HTML5 fullscreen api
            // https://developer.mozilla.org/en-US/docs/Web/API/Document/fullscreenElement
            // https://player.support.brightcove.com/brightcove-player/current-release/Player.html#event:fullscreenchange
            const videojsplayer = window['videojs']?.getPlayer(this.hostElement.querySelector('video-js').id);
            videojsplayer?.play();
            if (ev.detail.fullscreen) {
                videojsplayer?.requestFullscreen();
            }
        }
    }

    /**
     * Class private methods
     */
    private _addIntersectionObserver() {
        if (this.lazy && 'IntersectionObserver' in window) {
            this._intersectionObserver = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    // Remove intersection observer
                    this._removeIntersectionObserver();

                    //call getVideos to fetch the videoList
                    this._getVideos();
                }
            });
            if (this._intersectionObserver['USE_MUTATION_OBSERVER'] != null) {
                this._intersectionObserver['USE_MUTATION_OBSERVER'] = false;
            }
            this._intersectionObserver.observe(this.hostElement);
        }
    }

    /**
     * video created date is converted into time (ms) to sort the videos
     *
     * @param {string} createdDate - date string in (dd-mmm-yyyy) format (Ex: 17-Sep-2013)
     * @returns {number} - video created time in milliseconds
     */
    private _getCreatedTime(createdDate: string): number {
        // NOTE: firefox do not support date in (dd-mmm-yyyy) format
        // change created date format to Long date (dd mmm yyyy) replace '-' with ' ' (space) to fix the bug on firefox
        return (new Date(createdDate.replace(/-/g, ' ')).getTime());
    }

    /**
     * Get videos data from API call to endpoint
     */
    private async _getVideos() {
        let json: Video[];
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const videoEl: HTMLElement = this.hostElement.querySelector<HTMLScriptElement>('script[type=\'application/json\']');
        if (videoEl?.textContent?.trim()) {
            json = JSON.parse(videoEl.textContent.trim());
            if ('error' in json) {
                this.status = VideoPlayerStatus.ERROR;
            }
        } else {
            await fetch(this.apiUrl).then(response => {
                // check the response
                if (response.ok) {
                    // successful fetch
                    return response.json();
                } else {
                    // fetch failed, set status to error
                    this.status = VideoPlayerStatus.ERROR;
                    return Promise.reject(new Error('Received ${response.status}'));
                }
            }).then((data: Video[]) => {
                json = data;
            }).catch(() => {
                // update the status to ERROR if the API request fails
                this.status = VideoPlayerStatus.ERROR;
            });
        }
        if (json) {
                if (json.length > 0) {
                    // init and load bcov script
                    this._videoList = json;
                    this._initBcov();
                    this.status = VideoPlayerStatus.READY;
                } else {
                    // set status to empty
                    this.status = VideoPlayerStatus.EMPTY;
                }
            } else {
                // set status to error
                this.status = VideoPlayerStatus.ERROR;
        }


        // signal the media gallery
        if (this.hostElement.dataset.mediaType === 'video') {
            // fire event signalling video loaded
            this.tiMediaGalleryItemChange.emit({
                disabled: this.status !== VideoPlayerStatus.READY,
                loaded: true
            });
        }
    }

    /**
     * Init brightcove player scripts
     */
    private _initBcov() {
        // capture video-js element
        const videoEl: HTMLElement = this.hostElement.querySelector('video-js');

        if (videoEl) {
            // Brightcove player with Playlist
            if (!this.singleVideoPlayer) {
                // Brightcove player with playlist requires data-video-ids attribute
                // sort videos by createdDate to show latest video on the top
                this._videoList.sort((a: Video, b: Video) => {
                    return this._getCreatedTime(b.createdDate) - this._getCreatedTime(a.createdDate);
                });

                // get video-ids
                const videoIds = this._videoList.map((video: Video) => {
                    return video.id;
                }).join(',');
                // set data-video-ids using the videoList response
                videoEl.dataset.videoIds = videoIds;

            // Single Video Brightcove Player
            } else {
                // Brightcove single video player requires data-video-id attribute
                videoEl.dataset.videoId = this._videoList[0].id;
                videoEl.dataset.account = this._accountId;
                videoEl.dataset.player = 'sZbarZzBuz';

                // if being used inside media-gallery add media gallery API custom data properties
                if (this.hostElement.dataset.mediaType === 'video') {
                    this.hostElement.dataset.mediaName = this._videoList[0].name;
                    this.hostElement.dataset.mediaDesc = this._videoList[0].name;
                    this.hostElement.dataset.mediaThumbnailUrl = this._videoList[0].thumbnailURL;
                    this.hostElement.dataset.mediaImageUrl = this._videoList[0].videoStillURL;
                    this.hostElement.dataset.videoDesc = this._videoList[0].description;
                    if (this._videoList[0].transcript === 'yes') {
                        const region = (!this.locale || this.locale === 'en-US') ? '' : `/${this.locale.toLowerCase()}`;
                        this.hostElement.dataset.videoTranscriptUrl = `${region}/video/${this._videoList[0].id}#transcript-tab`;
                    }
                }
            }
        // update the status to ERROR if video-js element is not available
        // and return without loading the brightcove script
        } else {
            this.status = VideoPlayerStatus.ERROR;
            return;

        }

        const bcovSrc = this.singleVideoPlayer
            ? `//players.brightcove.net/${this._accountId}/sZbarZzBuz_default/index.min.js`
            : `//players.brightcove.net/${this._accountId}/experience_${this.experienceId}/live.js`;

        // Only append to head if Brightcove script has not been added yet
        if (!document.head.querySelector(`script[src="${bcovSrc}"]`)) {
            // create brightcove script element
            const bcovScript = document.createElement('script');
            bcovScript.setAttribute('src', bcovSrc);
            bcovScript.setAttribute('async', 'true');

            // Attach script load handler to initialize videos
            bcovScript.onload = async () => {
                document.querySelectorAll('ti-video-player').forEach((vpEl) => {
                    vpEl.initializeVideo();
                });
            };

            // attach to document head
            document.head.appendChild(bcovScript);
        }
        // Single player video player brightcove needs to initialize the video if brightcove script is already loaded
        // window['bc'] is loaded from single player Brightcove script (https://player.support.brightcove.com/coding-topics/using-bc-and-getplayer-methods.html)
        else if (window['bc'] && this.singleVideoPlayer) {
            window['bc'](videoEl);
            this._trackVideoLoadedMetrics();
            this._loadingContainer.style.display = 'none';
            this._playerContainer.setAttribute('class', 'ti-video-player-show-player');
        }
    }

    /**
     * Fire metrics event when video has been initialized and loaded and pass
     * video element for analytics to initilize their tracking code on the video element
     * (only needed for single video player experience as playlist player utilizes iframe that initializes it there)
     */
    private _trackVideoLoadedMetrics() {
        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: 'video initialized',
            data: {
                context: this.hostElement.querySelector('video')
            }
        });
    }

    /**
     * remove intersection observer
     */
    private _removeIntersectionObserver(): void {
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
            this._intersectionObserver = null;
        }
    }

    /**
     * render() function
     */
    render() {
        if (this.status === VideoPlayerStatus.EMPTY) {
            this.hostElement.innerHTML = '<!-- No videos available -->';
            return null;
        } else if (this.status === VideoPlayerStatus.ERROR){
            this.hostElement.outerHTML = '<!-- Videos load error -->';
            return null;
        } else {
            return (
                <Host>
                    <div class="ti-video-player-loader-container" ref={(el) => this._loadingContainer = el}><ti-progress></ti-progress></div>
                    <div class="ti-video-player-hide-player" ref={(el) => this._playerContainer = el}>
                        <div><slot name="title" /></div>
                        <slot />
                    </div>
                </Host>
            );
        }

    }
}
